import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Eye, EyeOff, CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import DepartmentDropdown from "./DepartmentDropdown";
import RoleDropdown from "./RoleDropdown";
import { jobTitleService } from "@/services/jobTitle.service";
import axiosInstance from "../../lib/axios";
import { getEmployeeDropdownOptions, getEmployeesForDropdown } from "@/services/employee.service";

// Helper function to format Date objects to yyyy-MM-dd for HTML input

// Helper function to calculate end date from start date and period in months
const calculateEndDate = (startDate: Date | string | null | undefined, periodInMonths: string): Date | null => {
  if (!startDate || !periodInMonths || periodInMonths.trim() === "") {
    return null;
  }

  try {
    const start = startDate instanceof Date ? startDate : new Date(startDate);
    const months = parseInt(periodInMonths.trim(), 10);

    if (isNaN(months) || months <= 0) {
      return null;
    }

    // Create a new date object to avoid mutating the original
    const endDate = new Date(start);

    // Add the specified number of months
    endDate.setMonth(endDate.getMonth() + months);

    // Subtract one day to get the last day of the period
    // For example: if start date is Jan 1 and period is 6 months, end date should be June 30
    endDate.setDate(endDate.getDate() - 1);

    return endDate;
  } catch (error) {
    console.error("Error calculating end date:", error);
    return null;
  }
};

// Helper function to calculate probation end date from joining date and probation period
const calculateProbationEndDate = (joiningDate: Date | string | null | undefined, probationPeriod: string): Date | null => {
  if (!joiningDate || !probationPeriod || probationPeriod.trim() === "") {
    return null;
  }

  try {
    const start = joiningDate instanceof Date ? joiningDate : new Date(joiningDate);
    const months = parseInt(probationPeriod.trim(), 10);

    if (isNaN(months) || months <= 0) {
      return null;
    }

    // Create a new date object to avoid mutating the original
    const endDate = new Date(start);

    // Add the specified number of months
    endDate.setMonth(endDate.getMonth() + months);

    return endDate;
  } catch (error) {
    console.error("Error calculating probation end date:", error);
    return null;
  }
};


// Define the employee schema using Zod
const employeeSchema = z
  .object({
    firstName: z.string().min(1, "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
    email: z.string().email("Invalid email address"),
    phone: z
      .string()
      .refine(
        (val) => !val || val.length >= 10,
        "Phone number must be at least 10 digits"
      )
      .refine(
        (val) => !val || val.length <= 15,
        "Phone number must be at most 15 digits"
      )
      .refine((val) => !val || /^[0-9+\-\s()]+$/.test(val), {
        message:
          "Phone number must contain only digits, +, -, spaces, and parentheses",
      }),
    department: z.string().min(1, "Department is required"),
    roles: z.array(z.string()).min(1, "At least one role is required"),
    employeeId: z
      .string()
      .min(1, "Employee ID is required")
      .refine((val) => /^EMP[0-9]{1,4}$/.test(val), {
        message:
          "Employee ID must be in format EMP1 or EMP0001 (EMP followed by 1-4 digits)",
      }),
    salary: z.number().min(1000, "Salary must be at least ₹1000"),
    jobInfo: z
      .object({
        joiningDate: z.date({
          required_error: "Joining date is required",
          invalid_type_error: "Please select a valid joining date",
        }),
        location: z.string().optional(),
        jobTitle: z.string().optional(),
        category: z.string().optional(),
        reportingManager: z.array(z.string()).optional(),
        lineManager: z.array(z.string()).optional(),
        lineManager2: z.array(z.string()).optional(),
        employmentStatus: z.string().optional(),
        probationPeriod: z.string().optional(),
        probationEndDate: z.date().optional(),
        feedbackRemainderDays: z.string().optional(),
        note: z.string().optional(),
      })
      .optional(),
    agreementInfo: z
      .object({
        startDate: z.date().optional(),
        endDate: z.date().optional(),
        periodInMonths: z.string().optional(),
        notifyPeriodInDays: z.string().optional(),
        note: z.string().optional(),
      })
      .optional(),
    compensationInfo: z
      .object({
        startDate: z.date().optional(),
        endDate: z.date().optional(),
        newSalary: z.string().optional(),
        payFrequency: z.string().optional(),
        noticePeriodInDays: z.string().optional(),
        notifyPeriodInDays: z.string().optional(),
        salaryMode: z.string().optional(),
        note: z.string().optional(),
      })
      .optional(),

    password: z.string().optional(),
    confirmPassword: z.string().optional(),
    departmentId: z.string().optional(),
    isEdit: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    // Password validation for add mode
    if (!data.isEdit) {
      if (!data.password || data.password.trim() === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Password is required",
          path: ["password"],
        });
      } else if (data.password.length < 8) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Password must be at least 8 characters long",
          path: ["password"],
        });
      } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(data.password)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Password must contain at least one uppercase letter, one lowercase letter, and one number",
          path: ["password"],
        });
      }

      if (!data.confirmPassword || data.confirmPassword.trim() === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Confirm password is required",
          path: ["confirmPassword"],
        });
      }
    }

    // Password matching validation - only if either password field has content
    if (
      (data.password && data.password.trim() !== "") ||
      (data.confirmPassword && data.confirmPassword.trim() !== "")
    ) {
      if (data.password !== data.confirmPassword) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Passwords don't match",
          path: ["confirmPassword"],
        });
      }
    }
  });

// Infer the type for form values
export type EmployeeFormValues = z.infer<typeof employeeSchema>;

// Define props for the EmployeeForm component
interface EmployeeFormProps {
  initialData?: Partial<EmployeeFormValues>;
  onSubmit: (data: EmployeeFormValues) => Promise<void>;
  onCancel: () => void;
  isEdit?: boolean;
  departments?: Array<{ _id?: string; id?: string; name: string }>;
  roles?: Array<{ _id?: string; id?: string; name: string }>;
}

// Helper function to normalize employee data
const normalizeEmployeeData = (data: any): Partial<EmployeeFormValues> => {
  if (!data) return {};

  // Extract user data if it exists, and also consider the top-level data object
  const user = data.user || {};
  const dataAndUser = { ...data, ...user };

  // Extract email
  const email = user.email || data.email || "";

  // IMPROVED: Extract phone number from multiple possible sources
  const phone = user.phone || data.phone || dataAndUser.phone || "";

  // Extract department data - IMPROVED LOGIC
  const department = data.department;
  let departmentName = "";
  let departmentId = "";

  if (typeof department === "object" && department !== null) {
    departmentName = department.name || department.title || "";
    departmentId = department._id || department.id || "";
  } else if (typeof department === "string") {
    departmentName = department;
    // Try to find the department ID from the name if we have departments available
    departmentId = "";
  }

  // If we have departmentId but no departmentName, try to get the name
  if (departmentId && !departmentName) {
    // This will be resolved later when departments are loaded
  }

  // If we have departmentName but no departmentId, try to get the ID
  if (departmentName && !departmentId) {
    // This will be resolved later when departments are loaded
  }

  // Extract roles data
  let roles: string[] = [];
  const rolesSource = user.roles || data.roles || data.role;
  if (Array.isArray(rolesSource)) {
    roles = rolesSource
      .map((role: any) =>
        typeof role === "object" ? role.name || role.title || "" : role
      )
      .filter(Boolean);
  } else if (rolesSource) {
    const role =
      typeof rolesSource === "object"
        ? rolesSource.name || rolesSource.title || ""
        : rolesSource;
    if (role) roles = [role];
  }
  const jobInfo = data.jobInfo || data.job || {};

  const normalizedJobInfo = {
    joiningDate: jobInfo.joiningDate
      ? new Date(jobInfo.joiningDate)
      : data.joiningDate
        ? new Date(data.joiningDate)
        : new Date(),
    location:
      jobInfo.location || jobInfo.jobLocation || jobInfo.workLocation || "",
    jobTitle: (() => {
      const jobTitle = jobInfo.jobTitle || jobInfo.title || jobInfo.position || jobInfo.role;
      if (!jobTitle) return "";
      if (typeof jobTitle === "string") return jobTitle;
      if (typeof jobTitle === "object" && jobTitle._id) return jobTitle._id;
      if (typeof jobTitle === "object" && jobTitle.id) return jobTitle.id;
      return "";
    })(),
    category:
      jobInfo.category || jobInfo.jobCategory || jobInfo.department || "",
    reportingManager: Array.isArray(jobInfo.reportingManager)
      ? jobInfo.reportingManager
      : jobInfo.reportingManager
        ? [jobInfo.reportingManager]
        : jobInfo.manager
          ? [jobInfo.manager]
          : jobInfo.supervisor
            ? [jobInfo.supervisor]
            : jobInfo.reportsTo
              ? [jobInfo.reportsTo]
              : [],
    lineManager: Array.isArray(jobInfo.lineManager)
      ? jobInfo.lineManager
      : jobInfo.lineManager
        ? [jobInfo.lineManager]
        : jobInfo.directManager
          ? [jobInfo.directManager]
          : [],
    lineManager2: Array.isArray(jobInfo.lineManager2)
      ? jobInfo.lineManager2
      : jobInfo.lineManager2
        ? [jobInfo.lineManager2]
        : jobInfo.secondaryManager
          ? [jobInfo.secondaryManager]
          : [],
    employmentStatus:
      jobInfo.employmentStatus || jobInfo.status || jobInfo.workStatus || "",
    probationPeriod: jobInfo.probationPeriod || "",
    probationEndDate: jobInfo.probationEndDate
      ? new Date(jobInfo.probationEndDate)
      : undefined,
    feedbackRemainderDays: jobInfo.feedbackRemainderDays || "",
    notifyDay: jobInfo.notifyDay || undefined,
    note:
      jobInfo.note ||
      jobInfo.notes ||
      jobInfo.comments ||
      jobInfo.description ||
      "",
  };

  // Normalize agreement info - IMPROVED with better data extraction
  const agreementInfo =
    data.agreementInfo || data.agreement || data.contract || {};

  const normalizedAgreementInfo = {
    startDate: agreementInfo.startDate
      ? new Date(agreementInfo.startDate)
      : undefined,
    endDate: agreementInfo.endDate
      ? new Date(agreementInfo.endDate)
      : undefined,
    periodInMonths:
      agreementInfo.periodInMonths ||
      agreementInfo.duration ||
      agreementInfo.period ||
      "",
    notifyPeriodInDays:
      agreementInfo.notifyPeriodInDays ||
      agreementInfo.noticePeriod ||
      agreementInfo.notificationPeriod ||
      "",
    note:
      agreementInfo.note || agreementInfo.notes || agreementInfo.comments || "",
  };

  // Normalize compensation info - IMPROVED with better data extraction
  const compensationInfo =
    data.compensationInfo || data.compensation || data.salary || {};

  const normalizedCompensationInfo = {
    startDate: compensationInfo.startDate
      ? new Date(compensationInfo.startDate)
      : undefined,
    endDate: compensationInfo.endDate
      ? new Date(compensationInfo.endDate)
      : undefined,
    newSalary:
      compensationInfo.newSalary ||
      compensationInfo.currentSalary ||
      compensationInfo.salary ||
      "",
    payFrequency:
      compensationInfo.payFrequency ||
      compensationInfo.frequency ||
      compensationInfo.paymentSchedule ||
      "",
    noticePeriodInDays:
      compensationInfo.noticePeriodInDays ||
      compensationInfo.noticePeriod ||
      "",
    notifyPeriodInDays:
      compensationInfo.notifyPeriodInDays ||
      compensationInfo.notificationPeriod ||
      "",

    salaryMode:
      compensationInfo.salaryMode ||
      compensationInfo.paymentMode ||
      compensationInfo.paymentMethod ||
      "",
    note:
      compensationInfo.note ||
      compensationInfo.notes ||
      compensationInfo.comments ||
      "",
  };



  // Parse joining date - IMPROVED with better date handling
  let joiningDate: Date | string = new Date();
  const dateSource = data.joiningDate || data.joinDate;
  if (dateSource) {
    try {
      joiningDate = new Date(dateSource);
      // Check if the date is valid
      if (isNaN(joiningDate.getTime())) {
        joiningDate = new Date();
      }
    } catch (error) {
      joiningDate = new Date();
    }
  }

  // IMPROVED: Extract password for edit mode auto-fill
  // For security, we don't auto-fill actual passwords, just show placeholder
  const password = ""; // Keep empty for security

  const normalized = {
    firstName: dataAndUser.firstName || "",
    lastName: dataAndUser.lastName || "",
    email: email,
    phone: phone,
    department: departmentId || departmentName, // Use ID first, fallback to name
    roles: roles,
    employeeId: dataAndUser.employeeId || "",
    salary: dataAndUser.salary || 1000,
    jobInfo: normalizedJobInfo,
    agreementInfo: normalizedAgreementInfo,
    compensationInfo: normalizedCompensationInfo,
    password: password, // Keep empty for security
    confirmPassword: password, // Keep empty for security
    departmentId: departmentId,
  };

  return normalized;
};

// Helper function to render multi-select employee input
const renderEmployeeMultiSelect = (
  value: string[],
  onChange: (value: string[]) => void,
  employees: Array<{ _id?: string; id?: string; name: string; employeeId?: string; email?: string }>,
  placeholder: string
) => {
  const selectedEmployees = employees.filter(emp =>
    value.includes(emp._id || emp.id || '')
  );


  const handleRemoveEmployee = (employeeId: string) => {
    onChange(value.filter(id => id !== employeeId));
  };

  return (
    <div className="space-y-2">
      <Select
        value=""
        onValueChange={(employeeId) => {
          if (employeeId && !value.includes(employeeId)) {
            onChange([...value, employeeId]);
          }
        }}
      >
        <SelectTrigger>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {employees
            .filter(emp => !value.includes(emp._id || emp.id || ''))
            .map((employee) => (
              <SelectItem key={employee._id || employee.id} value={employee._id || employee.id || ''}>
                {employee.name} ({employee.employeeId})
              </SelectItem>
            ))}
        </SelectContent>
      </Select>

      {selectedEmployees.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedEmployees.map((employee) => (
            <div
              key={employee._id || employee.id}
              className="flex items-center gap-1 bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-sm"
            >
              <span>{employee.name} ({employee.employeeId})</span>
              <button
                type="button"
                onClick={() => handleRemoveEmployee(employee._id || employee.id || '')}
                className="ml-1 text-blue-600 hover:text-blue-800"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// EmployeeForm component
function EmployeeForm({
  initialData,
  onSubmit,
  onCancel,
  isEdit = false,
  departments: departmentsProp = [],
  roles: rolesProp = [],
}: EmployeeFormProps) {
  // State for departments and roles data
  const [departments, setDepartments] =
    useState<Array<{ _id?: string; id?: string; name: string }>>(
      departmentsProp
    );
  const [roles, setRoles] =
    useState<Array<{ _id?: string; id?: string; name: string }>>(rolesProp);
  const [employees, setEmployees] =
    useState<Array<{ _id?: string; id?: string; name: string; employeeId?: string; email?: string }>>([]);
  const [jobTitles, setJobTitles] = useState<Array<{ id: string; title: string; description: string }>>([]);
  const [dropdownOptions, setDropdownOptions] = useState<{
    locations: string[];
    employmentStatuses: string[];
    payFrequencies: string[];
    salaryModes: string[];
    reduceExtendedOptions: string[];
  }>({
    locations: [],
    employmentStatuses: [],
    payFrequencies: [],
    salaryModes: [],
    reduceExtendedOptions: [],
  });

  // Keep local state in sync if parent supplies updated lists
  useEffect(() => {
    if (departmentsProp && departmentsProp.length) {
      setDepartments(departmentsProp);
    }
    if (rolesProp && rolesProp.length) {
      setRoles(rolesProp);
    }
  }, [departmentsProp, rolesProp]);

  // Fetch dropdown options from backend
  useEffect(() => {
    const fetchDropdownOptions = async () => {
      try {
        const options = await getEmployeeDropdownOptions();
        setDropdownOptions(options);

        // Fetch job titles
        const jobTitlesData = await jobTitleService.getJobTitlesForDropdown();
        setJobTitles(jobTitlesData);
      } catch (error) {
        setDropdownOptions({
          locations: [
            "Surat",
            "Ahmedabad",
            "Mumbai",
            "Bangalore",
            "Delhi",
            "Chennai",
            "Hyderabad",
            "Pune",
          ],
          employmentStatuses: [
            "Part-time",
            "Full-time",
            "Probation",
            "Contract",
            "Work from Home",
            "Intern",
            "Terminated",
          ],
          payFrequencies: [
            "Monthly",
            "Bi-weekly",
            "Weekly",
            "Quarterly",
            "Annually",
          ],
          salaryModes: ["Bank Transfer", "Cash", "Check", "Digital Payment"],
          reduceExtendedOptions: ["Yes", "No"],
        });
      }
    };

    fetchDropdownOptions();
  }, []);

  const [nextEmployeeId, setNextEmployeeId] = useState("");
  const [isGeneratingId, setIsGeneratingId] = useState(false);

  // Normalize and prepare initial data
  const normalizedInitialData = normalizeEmployeeData(initialData);

  const form = useForm<EmployeeFormValues>({
    resolver: zodResolver(employeeSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      department: "",
      roles: ["Employee"],
      employeeId: "",
      salary: 1000,
      jobInfo: {
        joiningDate: new Date(),
        location: "",
        jobTitle: "",
        category: "",
        reportingManager: [],
        lineManager: [],
        lineManager2: [],
        employmentStatus: "",
        probationPeriod: "",
        probationEndDate: undefined,
        feedbackRemainderDays: "",
        note: "",
      },
      agreementInfo: {
        startDate: undefined,
        endDate: undefined,
        periodInMonths: "",
        notifyPeriodInDays: "",
        note: "",
      },
      compensationInfo: {
        startDate: undefined,
        endDate: undefined,
        newSalary: "",
        payFrequency: "",
        noticePeriodInDays: "",
        notifyPeriodInDays: "",
        salaryMode: "",
        note: "",
      },
      password: "",
      confirmPassword: "",
      departmentId: "",
      isEdit: isEdit,
      ...normalizedInitialData,
    },
    mode: "onSubmit", // Only validate on form submission
  });

  // Effect to update form when normalizedInitialData changes - REMOVED to prevent conflicts
  // This logic is now handled in the main form reset useEffect below

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Function to generate next Employee ID
  const generateNextEmployeeId = React.useCallback(async () => {
    setIsGeneratingId(true);

    try {
      const response = await axiosInstance.get("/employees/next-id");
      const data = response.data;

      // Try different possible response structures
      let nextId = null;

      // Check various possible field names and structures
      if (data.nextEmployeeId) {
        nextId = data.nextEmployeeId;
      } else if (data.nextId) {
        nextId = data.nextId;
      } else if (data.next_id) {
        nextId = data.next_id;
      } else if (data.employeeId) {
        nextId = data.employeeId;
      } else if (data.id) {
        nextId = data.id;
      } else if (data.data && data.data.nextEmployeeId) {
        nextId = data.data.nextEmployeeId;
      } else if (data.data && data.data.nextId) {
        nextId = data.data.nextId;
      } else if (data.data && data.data.next_id) {
        nextId = data.data.next_id;
      } else if (data.data && data.data.employeeId) {
        nextId = data.data.employeeId;
      } else if (data.data && data.data.id) {
        nextId = data.data.id;
      } else if (typeof data === "string") {
        nextId = data;
      }

      if (!nextId) {
        throw new Error(
          `No next ID found in API response. Available fields: ${Object.keys(
            data
          ).join(", ")}`
        );
      }

      setNextEmployeeId(nextId);

      // Set the generated ID in the form if it's add mode
      if (!isEdit) {
        form.setValue("employeeId", nextId);
      }
    } catch (error) {
      const fallbackId = "EMP0001";
      setNextEmployeeId(fallbackId);

      if (!isEdit) {
        form.setValue("employeeId", fallbackId);
      }

      toast.error("Using default Employee ID", {
        description: `Started with ${fallbackId} (API unavailable)`,
      });
    } finally {
      setIsGeneratingId(false);
    }
  }, [isEdit, form]);

  // Fetch departments and roles once (unless provided via props) and generate next employee ID for add mode.
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch departments ONLY if not supplied via props
        let fetchedDepartments: any[] = [];
        if (!departmentsProp.length) {
          try {
            const deptResponse = await axiosInstance.get("/departments");
            const deptData = deptResponse.data;
            fetchedDepartments =
              deptData.data || (Array.isArray(deptData) ? deptData : []);
            if (!departments.length) setDepartments(fetchedDepartments);
          } catch (deptErr: any) {
            if (deptErr?.response?.status === 403) {
              // Silently ignore – use whatever we already have
            } else {
            }
          }
        }

        // Always attempt to fetch roles to ensure full list; ignore 403 if the user lacks permission
        try {
          const roleResponse = await axiosInstance.get("/roles");
          const roleData = roleResponse.data;
          const fetchedRoles =
            roleData.data || (Array.isArray(roleData) ? roleData : []);
          if (fetchedRoles.length) {
            // Merge with existing ensuring uniqueness by name/_id
            const combined = [...roles, ...fetchedRoles].reduce(
              (acc: any[], r: any) => {
                const id = r._id || r.id || r.name;
                if (!acc.find((x: any) => (x._id || x.id || x.name) === id)) {
                  acc.push(r);
                }
                return acc;
              },
              [] as any[]
            );
            setRoles(combined);
          }
        } catch (roleErr: any) {
          if (roleErr?.response?.status === 403) {
          } else {
          }
        }

        // Fetch employees for manager dropdowns
        try {
          const fetchedEmployees = await getEmployeesForDropdown();
          setEmployees(fetchedEmployees);
        } catch (empErr: any) {
          if (empErr?.response?.status === 403) {
            // Silently ignore permission errors
          } else {
            console.error("Failed to fetch employees:", empErr);
          }
        }
      } catch (error) { }
    };

    fetchData();

    if (!isEdit) {
      generateNextEmployeeId();
    }
  }, [isEdit, generateNextEmployeeId, departments.length, roles.length]);

  // Reset form when initialData changes (important for edit mode)
  useEffect(() => {
    if (initialData && isEdit) {
      const normalized = normalizeEmployeeData(initialData);

      // IMPROVED: Set department value to match dropdown expectation
      let departmentValue = normalized.department || "";

      // If we have departments loaded, try to find the matching department
      if (departments.length > 0) {
        // First try to match by ID if we have one
        if (normalized.departmentId) {
          const matchingDept = departments.find(
            (dept) =>
              dept._id === normalized.departmentId ||
              dept.id === normalized.departmentId
          );
          if (matchingDept) {
            // Use the ID that the dropdown expects
            departmentValue =
              matchingDept._id || matchingDept.id || matchingDept.name;
          }
        }

        // If no match by ID, try to find by name
        if (!departmentValue || departmentValue === normalized.department) {
          const matchingDept = departments.find(
            (dept) => dept.name === normalized.department
          );
          if (matchingDept) {
            departmentValue =
              matchingDept._id || matchingDept.id || matchingDept.name;
          }
        }
      }

      // IMPROVED: Handle roles resolution - convert role names to IDs for dropdown
      let rolesValue = normalized.roles || [];

      if (
        Array.isArray(rolesValue) &&
        rolesValue.length > 0 &&
        roles.length > 0
      ) {
        // For the dropdown, we need to find the first role and convert it to the format expected by RoleDropdown
        const firstRoleName = rolesValue[0];
        const matchingRole = roles.find((role) => role.name === firstRoleName);
        if (matchingRole) {
          // RoleDropdown expects the role name as value, not ID
          rolesValue = [matchingRole.name];
        } else {
          // If no match found, keep the original role name
          rolesValue = [firstRoleName];
        }
      } else {
      }

      // IMPROVED: Ensure all nested objects are properly set with fallbacks
      const formData = {
        ...form.getValues(),
        ...normalized,
        department: departmentValue,
        roles: rolesValue,
        isEdit: true,
        // Ensure nested objects are properly populated with fallbacks - FIXED LOGIC
        jobInfo: {
          joiningDate: normalized.jobInfo?.joiningDate || new Date(),
          location: normalized.jobInfo?.location || "",
          jobTitle: (() => {
            const jobTitle = normalized.jobInfo?.jobTitle;
            if (!jobTitle) return "";
            if (typeof jobTitle === "string") return jobTitle;
            if (typeof jobTitle === "object" && (jobTitle as any)._id) return (jobTitle as any)._id;
            if (typeof jobTitle === "object" && (jobTitle as any).id) return (jobTitle as any).id;
            return "";
          })(),
          category: normalized.jobInfo?.category || "",
          reportingManager: normalized.jobInfo?.reportingManager || [],
          lineManager: normalized.jobInfo?.lineManager || [],
          lineManager2: normalized.jobInfo?.lineManager2 || [],
          employmentStatus: normalized.jobInfo?.employmentStatus || "",
          probationPeriod: normalized.jobInfo?.probationPeriod || "",
          probationEndDate: normalized.jobInfo?.probationEndDate || undefined,
          feedbackRemainderDays: normalized.jobInfo?.feedbackRemainderDays || "",
          note: normalized.jobInfo?.note || "",
        },
        agreementInfo: {
          startDate: normalized.agreementInfo?.startDate || undefined,
          endDate: normalized.agreementInfo?.endDate || undefined,
          periodInMonths: normalized.agreementInfo?.periodInMonths || "",
          notifyPeriodInDays:
            normalized.agreementInfo?.notifyPeriodInDays || "",
          note: normalized.agreementInfo?.note || "",
        },
        compensationInfo: {
          startDate: normalized.compensationInfo?.startDate || undefined,
          endDate: normalized.compensationInfo?.endDate || undefined,
          newSalary: normalized.compensationInfo?.newSalary || "",
          payFrequency: normalized.compensationInfo?.payFrequency || "",
          noticePeriodInDays:
            normalized.compensationInfo?.noticePeriodInDays || "",
          notifyPeriodInDays:
            normalized.compensationInfo?.notifyPeriodInDays || "",
          salaryMode: normalized.compensationInfo?.salaryMode || "",
          note: normalized.compensationInfo?.note || "",
        },
      };



      // Reset form with all data at once to prevent conflicts
      form.reset(formData, {
        keepDefaultValues: false,
        keepDirty: false,
        keepTouched: false,
      });

      // Explicitly set the roles field to ensure it's properly tracked
      if (rolesValue.length > 0) {
        form.setValue("roles", rolesValue, {
          shouldDirty: true,
          shouldTouch: true,
        });
      }

      // Explicitly set nested object values to ensure they are properly tracked
      if (formData.jobInfo) {
        Object.keys(formData.jobInfo).forEach((key) => {
          const value = formData.jobInfo[key as keyof typeof formData.jobInfo];
          if (value !== undefined) {
            form.setValue(`jobInfo.${key}` as any, value, {
              shouldDirty: true,
              shouldTouch: true,
            });
          }
        });
      }

      if (formData.compensationInfo) {
        Object.keys(formData.compensationInfo).forEach((key) => {
          const value =
            formData.compensationInfo[
            key as keyof typeof formData.compensationInfo
            ];
          if (value !== undefined) {
            form.setValue(`compensationInfo.${key}` as any, value, {
              shouldDirty: true,
              shouldTouch: true,
            });
          }
        });
      }



      // Trigger validation after reset
      form.trigger();
    }
  }, [initialData, isEdit, form, departments, roles]);

  // Additional effect to handle when departments/roles are loaded after initial data
  // This is now handled in the main form reset useEffect to prevent conflicts

  useEffect(() => {
    if (isEdit && initialData) {
      const subscription = form.watch((_value, { type, name }) => {
        if (type === "change") {
          // Log specific dropdown changes
          if (name && name.includes(".")) {
            const [] = name.split(".");
          }
        }
      });
      return () => subscription.unsubscribe();
    }
  }, [isEdit, initialData, form]);

  // Effect to automatically calculate end date when start date or period in months changes
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      // Check if startDate or periodInMonths in agreementInfo changed
      if (name === "agreementInfo.startDate" || name === "agreementInfo.periodInMonths") {
        const startDate = value.agreementInfo?.startDate;
        const periodInMonths = value.agreementInfo?.periodInMonths;

        if (startDate && periodInMonths) {
          const calculatedEndDate = calculateEndDate(startDate, periodInMonths);
          if (calculatedEndDate) {
            // Update the end date field
            form.setValue("agreementInfo.endDate", calculatedEndDate, {
              shouldDirty: true,
              shouldTouch: true,
            });
          }
        }
      }

      // Check if probation-related fields changed
      if (name === "jobInfo.employmentStatus" || name === "jobInfo.joiningDate" || name === "jobInfo.probationPeriod") {
        const employmentStatus = value.jobInfo?.employmentStatus;
        const joiningDate = value.jobInfo?.joiningDate;
        const probationPeriod = value.jobInfo?.probationPeriod;
        // Auto-calculate probation end date if employment status is "Probation" and both joining date and probation period are provided
        if (employmentStatus === "Probation" && joiningDate && probationPeriod && probationPeriod.trim() !== "") {
          const calculatedProbationEndDate = calculateProbationEndDate(joiningDate, probationPeriod);

          if (calculatedProbationEndDate) {
            form.setValue("jobInfo.probationEndDate", calculatedProbationEndDate, {
              shouldDirty: true,
              shouldTouch: true,
            });
          }
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [form]);

  const handleFormSubmit = async (data: EmployeeFormValues) => {
    setIsSubmitting(true);

    try {
      // Debug logging to help identify the issue

      // Pass the raw data directly to the parent component for processing
      // The AddEmployeeDialog.handleSubmit will handle all the data transformation
      await onSubmit(data);
    } catch (error) {
      if (error && typeof error === "object" && "message" in error) {
        const errorMessage = (error as any).message?.toLowerCase() || "";
        if (errorMessage.includes("email")) {
          toast.error("Email Update Failed", {
            description: "The email address may already be in use or invalid. Please try a different email.",
          });
          return; // Don't re-throw, we handled it
        }
      }

      // Don't show error here, let parent handle it
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFormError = (errors: any) => {
    const errorFields = Object.keys(errors);
    if (errorFields.length > 0) {
      const firstError = errors[errorFields[0]];
      const errorMessage =
        firstError?.message || "Please check all required fields";

      toast.error("Form validation failed", {
        description: errorMessage,
      });
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleFormSubmit, handleFormError)}
        className="space-y-6 p-1"
      >
        {/* Personal Information Section */}
        <div className="space-y-4 pb-2">
          <h3 className="text-lg font-semibold">Personal Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    First Name <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="John" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Last Name <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Doe" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Email <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="john.doe@example.com"
                      {...field}
                      onChange={(e) => {
                        field.onChange(e.target.value);

                        // Trigger form validation immediately
                        setTimeout(() => {
                          const formErrors = form.formState.errors;
                          if (formErrors.email) {
                            // Email validation error exists
                          } else {
                            // Email validation passed
                          }
                        }, 0);
                      }}
                      onBlur={() => {
                        field.onBlur();
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                  {isEdit && (
                    <p className="text-xs text-blue-600 mt-1">
                      Current: {field.value || "No email set"}
                    </p>
                  )}
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Phone <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="tel"
                      placeholder="+91 9876543210"
                      {...field}
                      onChange={(e) => {
                        let value = e.target.value;

                        // Remove all non-digits
                        const digitsOnly = value.replace(/\D/g, "");

                        // If user starts typing without +91, add it
                        if (digitsOnly.length > 0 && !value.startsWith("+91")) {
                          if (
                            digitsOnly.startsWith("91") &&
                            digitsOnly.length > 2
                          ) {
                            // If starts with 91, format as +91
                            value = "+91 " + digitsOnly.substring(2);
                          } else {
                            // Add +91 prefix
                            value = "+91 " + digitsOnly;
                          }
                        } else if (value.startsWith("+91")) {
                          // Format existing +91 number
                          const phoneDigits = digitsOnly.substring(2); // Remove 91
                          if (phoneDigits.length > 0) {
                            value = "+91 " + phoneDigits;
                          } else {
                            value = "+91 ";
                          }
                        }

                        // Limit to +91 + 10 digits
                        if (digitsOnly.length > 12) {
                          const limitedDigits = digitsOnly.substring(0, 12);
                          value = "+91 " + limitedDigits.substring(2);
                        }

                        field.onChange(value);
                      }}
                      maxLength={14} // +91 + space + 10 digits
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {isEdit ? (
                      "New Password (optional)"
                    ) : (
                      <>
                        Password <span className="text-red-500">*</span>
                      </>
                    )}
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        placeholder={
                          isEdit
                            ? "Leave empty to keep current password"
                            : "Enter strong password"
                        }
                        {...field}
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </FormControl>
                  <FormMessage />
                  {!isEdit && (
                    <p className="text-sm text-gray-500 mt-1">
                      Must be at least 8 characters with uppercase, lowercase,
                      and number
                    </p>
                  )}
                  {isEdit && (
                    <p className="text-sm text-gray-500 mt-1">
                      Only enter a password if you want to change it
                    </p>
                  )}
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {isEdit ? (
                      "Confirm New Password"
                    ) : (
                      <>
                        Confirm Password <span className="text-red-500">*</span>
                      </>
                    )}
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder={
                          isEdit ? "Confirm new password" : "Re-enter password"
                        }
                        {...field}
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        {/* Employment Details Section */}
        <div className="space-y-4 pb-2">
          <h3 className="text-lg font-semibold">Employment Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="department"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Department <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <DepartmentDropdown
                      value={field.value || ""}
                      onValueChange={field.onChange}
                      departments={departments}
                      className={form.formState.errors.department ? "border-red-500 focus:border-red-500" : ""}
                      placeholder="Select Department"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="roles"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Roles <span className="text-red-500">*</span></FormLabel>
                  <FormControl>
                    <RoleDropdown
                      value={
                        Array.isArray(field.value) && field.value.length > 0
                          ? field.value[0]
                          : "no-role"
                      }
                      onValueChange={(selectedRoleName: string) => {
                        if (
                          selectedRoleName &&
                          selectedRoleName.trim() !== "" &&
                          selectedRoleName !== "no-role"
                        ) {
                          field.onChange([selectedRoleName]);
                        } else {
                          field.onChange([]);
                        }
                      }}
                      roles={roles}
                      className={form.formState.errors.roles ? "border-red-500 focus:border-red-500" : ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="employeeId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Employee ID <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        placeholder="EMP1 or EMP0001"
                        {...field}
                        onChange={(e) => {
                          // Auto-format the input to match EMP#### pattern
                          let value = e.target.value.toUpperCase();

                          // Remove any non-alphanumeric characters except EMP
                          value = value.replace(/[^EMP0-9]/g, "");

                          // Ensure it starts with EMP
                          if (!value.startsWith("EMP")) {
                            if (value.length > 0 && !"EMP".startsWith(value)) {
                              value = "EMP" + value.replace(/[^0-9]/g, "");
                            } else {
                              value = "EMP";
                            }
                          }

                          // Limit to EMP + 4 digits max
                          if (value.length > 7) {
                            value = value.substring(0, 7);
                          }

                          field.onChange(value);
                        }}
                        maxLength={7}
                        style={{ textTransform: "uppercase" }}
                        disabled={isGeneratingId || isEdit}
                        className={isEdit ? "bg-gray-100" : ""}
                      />
                      {isGeneratingId && (
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        </div>
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                  <p className="text-sm text-gray-500 mt-1">
                    {isEdit ? (
                      <span>Employee ID cannot be changed</span>
                    ) : (
                      <>
                        Format: EMP1
                        {nextEmployeeId && !isGeneratingId && (
                          <span className="ml-2 text-blue-600">
                            Next: {nextEmployeeId}
                          </span>
                        )}
                        {isGeneratingId && (
                          <span className="ml-2 text-blue-600">
                            Generating...
                          </span>
                        )}
                      </>
                    )}
                  </p>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="salary"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Salary (₹) <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="50000"
                      min="1000"
                      step="1000"
                      value={field.value ?? ""}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                  <p className="text-sm text-gray-500 mt-1">
                    Minimum salary: ₹1,000
                  </p>
                </FormItem>
              )}
            />
          </div>
        </div>

        {/* Job Information Section */}
        <div className="space-y-4 pb-2">
          <h3 className="text-lg font-semibold">Job Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="jobInfo.joiningDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Joining Date <span className="text-red-500">*</span>
                  </FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full pl-3 text-left font-normal",
                            !field.value && "text-muted-foreground"
                          )}
                        >
                          {field.value
                            ? format(new Date(field.value), "dd/MM/yyyy")
                            : <span>Pick a date</span>}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value ? new Date(field.value) : undefined}
                        onSelect={(date) => field.onChange(date ?? null)}
                        initialFocus
                        captionLayout="dropdown"
                        startMonth={new Date(1950, 0)}
                        endMonth={new Date(2050, 11)}
                      />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="jobInfo.location"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Location</FormLabel>
                  <FormControl>
                    <Select
                      key={`location-${field.value || "empty"}`}
                      value={field.value || ""}
                      onValueChange={(value) => {
                        field.onChange(value === "clear" ? "" : value);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select location" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="clear">
                          <span className="text-gray-500 italic">
                            Clear selection
                          </span>
                        </SelectItem>
                        {dropdownOptions.locations.map((location) => (
                          <SelectItem key={location} value={location}>
                            {location}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="jobInfo.jobTitle"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Job Title</FormLabel>
                  <FormControl>
                    <Select
                      value={field.value || ""}
                      onValueChange={field.onChange}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select job title" />
                      </SelectTrigger>
                      <SelectContent>
                        {jobTitles.map((jobTitle) => (
                          <SelectItem key={jobTitle.id} value={jobTitle.id}>
                            {jobTitle.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="jobInfo.category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Category</FormLabel>
                  <FormControl>
                    <Input placeholder="IT" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="jobInfo.reportingManager"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reporting Manager</FormLabel>
                  <FormControl>
                    {renderEmployeeMultiSelect(
                      field.value || [],
                      field.onChange,
                      employees,
                      "Select reporting manager..."
                    )}
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="jobInfo.lineManager"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Line Manager</FormLabel>
                  <FormControl>
                    {renderEmployeeMultiSelect(
                      field.value || [],
                      field.onChange,
                      employees,
                      "Select line manager..."
                    )}
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="jobInfo.lineManager2"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Line Manager 2</FormLabel>
                  <FormControl>
                    {renderEmployeeMultiSelect(
                      field.value || [],
                      field.onChange,
                      employees,
                      "Select line manager 2..."
                    )}
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="jobInfo.employmentStatus"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Employment Status</FormLabel>
                  <FormControl>
                    <Select
                      key={`job-employment-status-${field.value || "empty"}`}
                      value={field.value || ""}
                      onValueChange={(value) => {
                        field.onChange(value === "clear" ? "" : value);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select employment status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="clear">
                          <span className="text-gray-500 italic">
                            Clear selection
                          </span>
                        </SelectItem>
                        {dropdownOptions.employmentStatuses.map((status) => (
                          <SelectItem key={status} value={status}>
                            {status}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Probation Period - Only show when employment status is "Probation" */}
            {form.watch("jobInfo.employmentStatus") === "Probation" && (
              <FormField
                control={form.control}
                name="jobInfo.probationPeriod"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Probation Period (Months) <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="3"
                        min="1"
                        max="24"
                        {...field}
                        onChange={(e) => {
                          const value = e.target.value;
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                    <p className="text-sm text-gray-500 mt-1">
                      Required when employment status is "Probation"
                    </p>
                  </FormItem>
                )}
              />
            )}

            {/* Probation End Date - Only show when employment status is "Probation" */}
            {form.watch("jobInfo.employmentStatus") === "Probation" && (
              <FormField
                control={form.control}
                name="jobInfo.probationEndDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Probation End Date <span className="text-red-500">*</span>
                    </FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value
                              ? format(new Date(field.value), "dd/MM/yyyy")
                              : <span>Pick a date</span>}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value ? new Date(field.value) : undefined}
                          onSelect={(date) => field.onChange(date ?? null)}
                          initialFocus
                          captionLayout="dropdown"
                          startMonth={new Date(1950, 0)}
                          endMonth={new Date(2050, 11)}
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                    <p className="text-sm text-gray-500 mt-1">
                      Auto-calculated from joining date and probation period, but you can edit manually
                    </p>
                  </FormItem>
                )}
              />
            )}

            {/* Feedback Remainder Days - Only show when employment status is "Probation" */}
            {form.watch("jobInfo.employmentStatus") === "Probation" && (
              <FormField
                control={form.control}
                name="jobInfo.feedbackRemainderDays"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Feedback Remainder Days</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="7"
                        min="1"
                        max="30"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                    <p className="text-sm text-gray-500 mt-1">
                      Days before probation end date to send feedback reminder
                    </p>
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="jobInfo.note"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Note</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Additional notes for the job"
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        {/* Agreement Information Section */}
        <div className="space-y-4 pb-2">
          <h3 className="text-lg font-semibold">Agreement Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="agreementInfo.startDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Start Date</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full pl-3 text-left font-normal",
                            !field.value && "text-muted-foreground"
                          )}
                        >
                          {field.value
                            ? format(new Date(field.value), "dd/MM/yyyy")
                            : <span>Pick a date</span>}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value ? new Date(field.value) : undefined}
                        onSelect={(date) => {
                          const newDate = date ?? null;
                          field.onChange(newDate);
                          // Trigger end date calculation if period in months is also set
                          const periodInMonths = form.getValues("agreementInfo.periodInMonths");
                          if (newDate && periodInMonths) {
                            const calculatedEndDate = calculateEndDate(newDate, periodInMonths);
                            if (calculatedEndDate) {
                              form.setValue("agreementInfo.endDate", calculatedEndDate, {
                                shouldDirty: true,
                                shouldTouch: true,
                              });
                            }
                          }
                        }}
                        initialFocus
                        captionLayout="dropdown"
                        startMonth={new Date(1950, 0)}
                        endMonth={new Date(2050, 11)}
                      />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="agreementInfo.endDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>End Date</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full pl-3 text-left font-normal",
                            !field.value && "text-muted-foreground"
                          )}
                        >
                          {field.value
                            ? format(new Date(field.value), "dd/MM/yyyy")
                            : <span>Pick a date</span>}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value ? new Date(field.value) : undefined}
                        onSelect={(date) => field.onChange(date ?? null)}
                        initialFocus
                        captionLayout="dropdown"
                        startMonth={new Date(1950, 0)}
                        endMonth={new Date(2050, 11)}
                      />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="agreementInfo.periodInMonths"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Period in Months</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="6"
                      {...field}
                      onChange={(e) => {
                        const value = e.target.value;
                        field.onChange(value);

                        // Trigger end date calculation if start date is also set
                        const startDate = form.getValues("agreementInfo.startDate");
                        if (startDate && value) {
                          const calculatedEndDate = calculateEndDate(startDate, value);
                          if (calculatedEndDate) {
                            form.setValue("agreementInfo.endDate", calculatedEndDate, {
                              shouldDirty: true,
                              shouldTouch: true,
                            });
                          }
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="agreementInfo.notifyPeriodInDays"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notify Period in Days</FormLabel>
                  <FormControl>
                    <Input placeholder="30" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="agreementInfo.note"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Note</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Additional notes for the agreement"
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        {/* Compensation Information Section */}
        <div className="space-y-4 pb-2">
          <h3 className="text-lg font-semibold">Compensation Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="compensationInfo.startDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Start Date</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full pl-3 text-left font-normal",
                            !field.value && "text-muted-foreground"
                          )}
                        >
                          {field.value
                            ? format(new Date(field.value), "dd/MM/yyyy")
                            : <span>Pick a date</span>}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value ? new Date(field.value) : undefined}
                        onSelect={(date) => field.onChange(date ?? null)}
                        initialFocus
                        captionLayout="dropdown"
                        startMonth={new Date(1950, 0)}
                        endMonth={new Date(2050, 11)}
                      />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="compensationInfo.endDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>End Date</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full pl-3 text-left font-normal",
                            !field.value && "text-muted-foreground"
                          )}
                        >
                          {field.value
                            ? format(new Date(field.value), "dd/MM/yyyy")
                            : <span>Pick a date</span>}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value ? new Date(field.value) : undefined}
                        onSelect={(date) => field.onChange(date ?? null)}
                        initialFocus
                        captionLayout="dropdown"
                        startMonth={new Date(1950, 0)}
                        endMonth={new Date(2050, 11)}
                      />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="compensationInfo.newSalary"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>New Salary</FormLabel>
                  <FormControl>
                    <Input placeholder="60000" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="compensationInfo.payFrequency"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Pay Frequency</FormLabel>
                  <FormControl>
                    <Select
                      key={`pay-frequency-${field.value || "empty"}`}
                      value={field.value || ""}
                      onValueChange={(value) =>
                        field.onChange(value === "clear" ? "" : value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select pay frequency" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="clear">
                          <span className="text-gray-500 italic">
                            Clear selection
                          </span>
                        </SelectItem>
                        {dropdownOptions.payFrequencies.map((frequency) => (
                          <SelectItem key={frequency} value={frequency}>
                            {frequency}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="compensationInfo.noticePeriodInDays"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notice Period in Days</FormLabel>
                  <FormControl>
                    <Input placeholder="30" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="compensationInfo.notifyPeriodInDays"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notify Period in Days</FormLabel>
                  <FormControl>
                    <Input placeholder="30" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="compensationInfo.salaryMode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Salary Mode</FormLabel>
                  <FormControl>
                    <Select
                      key={`salary-mode-${field.value || "empty"}`}
                      value={field.value || ""}
                      onValueChange={(value) =>
                        field.onChange(value === "clear" ? "" : value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select salary mode" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="clear">
                          <span className="text-gray-500 italic">
                            Clear selection
                          </span>
                        </SelectItem>
                        {dropdownOptions.salaryModes.map((mode) => (
                          <SelectItem key={mode} value={mode}>
                            {mode}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="compensationInfo.note"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Note</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Additional notes for compensation"
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>



        <div className="flex justify-end space-x-4 pt-6 pb-2 border-t border-gray-200 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting
              ? isEdit
                ? "Updating Employee..."
                : "Adding Employee..."
              : isEdit
                ? "Update Employee"
                : "Add Employee"}
          </Button>
        </div>
      </form>
    </Form>
  );
}

// Define props for the AddEmployeeDialog component
interface AddEmployeeDialogProps {
  mode: "add" | "edit";
  employee?: EmployeeFormValues;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onEmployeeEdit?: (data: EmployeeFormValues) => Promise<void>;
  onRefresh?: () => Promise<void>;
  departments?: Array<{ _id?: string; id?: string; name: string }>;
  roles?: Array<{ _id?: string; id?: string; name: string }>;
}

// AddEmployeeDialog component
export function AddEmployeeDialog({
  mode = "add",
  employee,
  open,
  onOpenChange,
  onEmployeeEdit,
  onRefresh,
  departments,
  roles,
}: AddEmployeeDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dialogOpen = open !== undefined ? open : isOpen;
  const setDialogOpen = onOpenChange || setIsOpen;

  // Add a ref to reset the form after successful add
  const [formKey, setFormKey] = useState(0);

  const handleCancel = () => {
    // Reset form by incrementing key
    setFormKey((prev) => prev + 1);

    // Close dialog
    setDialogOpen(false);
  };

  const handleSubmit = async (data: EmployeeFormValues) => {
    try {
      // Exclude confirmPassword from API data
      const { confirmPassword, ...dataWithoutConfirmPassword } = data;

      // Convert department value to ID – handle both name and ID cases
      let departmentId: string = "";

      // First try to find by ID (in case the dropdown stored the ID)
      const selectedDepartmentById = departments?.find(
        (dept) =>
          dept._id === dataWithoutConfirmPassword.department ||
          dept.id === dataWithoutConfirmPassword.department
      );

      if (selectedDepartmentById) {
        departmentId =
          selectedDepartmentById._id || selectedDepartmentById.id || "";
      } else {
        // If not found by ID, try to find by name
        const selectedDepartmentByName = departments?.find(
          (dept) => dept.name === dataWithoutConfirmPassword.department
        );
        if (selectedDepartmentByName) {
          const rawDeptId =
            selectedDepartmentByName._id || selectedDepartmentByName.id;
          if (typeof rawDeptId === "string") {
            departmentId = rawDeptId;
          }
        }
      }

      // Convert role name to ID (assuming single role for now) – only keep if we have a valid string ID
      const selectedRole = roles?.find(
        (role) =>
          role.name ===
          (Array.isArray(dataWithoutConfirmPassword.roles)
            ? dataWithoutConfirmPassword.roles[0]
            : dataWithoutConfirmPassword.roles)
      );
      const roleIds: string[] = [];
      if (selectedRole) {
        const rawRoleId = selectedRole._id || selectedRole.id;
        if (typeof rawRoleId === "string") {
          roleIds.push(rawRoleId);
        }
      }

      // Transform the data to match API expectations exactly
      const apiData = {
        firstName: dataWithoutConfirmPassword.firstName,
        lastName: dataWithoutConfirmPassword.lastName,
        email: dataWithoutConfirmPassword.email,
        phone: dataWithoutConfirmPassword.phone || "",
        employeeId: dataWithoutConfirmPassword.employeeId,
        salary:
          typeof dataWithoutConfirmPassword.salary === "string"
            ? Number(dataWithoutConfirmPassword.salary)
            : dataWithoutConfirmPassword.salary,
        roles: roleIds,
        departmentId: departmentId,
        // Send complete nested objects with all fields to ensure proper backend processing
        // Only send nested objects if they have at least one field with data
        jobInfo: (() => {
          const jobInfo = dataWithoutConfirmPassword.jobInfo || {} as any;
          const cleaned: any = {};

          // Always include all fields, even if empty, to ensure proper backend processing
          cleaned.joiningDate = (jobInfo as any).joiningDate || new Date();
          cleaned.location = (jobInfo as any).location?.trim() || "";
          cleaned.jobTitle = (() => {
            const jobTitle = (jobInfo as any).jobTitle;
            if (!jobTitle) return "";
            if (typeof jobTitle === "string") return jobTitle.trim();
            if (typeof jobTitle === "object" && jobTitle._id) return jobTitle._id;
            if (typeof jobTitle === "object" && jobTitle.id) return jobTitle.id;
            return "";
          })();
          cleaned.category = (jobInfo as any).category?.trim() || "";
          cleaned.reportingManager = (jobInfo as any).reportingManager || [];
          cleaned.lineManager = (jobInfo as any).lineManager || [];
          cleaned.lineManager2 = (jobInfo as any).lineManager2 || [];
          cleaned.employmentStatus = (jobInfo as any).employmentStatus?.trim() || "";
          cleaned.probationPeriod = (jobInfo as any).probationPeriod?.trim() || "";
          cleaned.probationEndDate = (jobInfo as any).probationEndDate || undefined;
          cleaned.feedbackRemainderDays = (jobInfo as any).feedbackRemainderDays?.trim() || "";
          cleaned.note = (jobInfo as any).note?.trim() || "";

          // Only return the object if it has at least one non-empty field
          const hasData = Object.values(cleaned).some(
            (value) => value !== undefined && value !== null && value !== ""
          );
          return hasData ? cleaned : undefined;
        })(),
        agreementInfo: (() => {
          const agreementInfo = dataWithoutConfirmPassword.agreementInfo || {};
          const cleaned: any = {};

          // Always include all fields, even if empty, to ensure proper backend processing
          cleaned.startDate = agreementInfo.startDate || undefined;
          cleaned.endDate = agreementInfo.endDate || undefined;
          cleaned.periodInMonths = agreementInfo.periodInMonths?.trim() || "";
          cleaned.notifyPeriodInDays =
            agreementInfo.notifyPeriodInDays?.trim() || "";
          cleaned.note = agreementInfo.note?.trim() || "";

          // Only return the array if it has at least one non-empty field
          const hasData = Object.values(cleaned).some(
            (value) => value !== undefined && value !== null && value !== ""
          );
          return hasData ? [cleaned] : undefined;
        })(),
        compensationInfo: (() => {
          const compensationInfo =
            dataWithoutConfirmPassword.compensationInfo || {};
          const cleaned: any = {};

          // Always include all fields, even if empty, to ensure proper backend processing
          cleaned.startDate = compensationInfo.startDate || undefined;
          cleaned.endDate = compensationInfo.endDate || undefined;
          cleaned.newSalary = compensationInfo.newSalary?.trim() || "";
          cleaned.payFrequency = compensationInfo.payFrequency?.trim() || "";
          cleaned.noticePeriodInDays =
            compensationInfo.noticePeriodInDays?.trim() || "";
          cleaned.notifyPeriodInDays =
            compensationInfo.notifyPeriodInDays?.trim() || "";

          cleaned.salaryMode = compensationInfo.salaryMode?.trim() || "";
          cleaned.note = compensationInfo.note?.trim() || "";

          // Only return the array if it has at least one non-empty field
          const hasData = Object.values(cleaned).some(
            (value) => value !== undefined && value !== null && value !== ""
          );
          return hasData ? [cleaned] : undefined;
        })(),

      };

      if (
        dataWithoutConfirmPassword.password &&
        dataWithoutConfirmPassword.password.trim() !== ""
      ) {
        (apiData as any).password = dataWithoutConfirmPassword.password;
      }

      if (mode === "add") {
        // Make direct API call for add mode
        await axiosInstance.post("/employees", apiData);

        toast.success("Employee added successfully!");
        setDialogOpen(false);
        setFormKey((prev) => prev + 1); // reset form

        // Call the refresh callback if provided
        if (onRefresh) {
          try {
            await onRefresh();
          } catch (error) {
            // Refresh callback completed
          }
        }
      } else if (onEmployeeEdit) {
        // For edit mode, use the callback with the transformed data
        await onEmployeeEdit(apiData as any);
        setDialogOpen(false);
      }
    } catch (error: any) {
      // Handled globally
    }
  };

  const handleAddEmployeeClick = () => {
    setDialogOpen(true);
  };

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      {mode === "add" && (
        <DialogTrigger asChild>
          <Button
            onClick={
              open !== undefined
                ? handleAddEmployeeClick
                : () => setDialogOpen(true)
            }
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Employee
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-w-7xl max-h-[95vh] overflow-hidden">
        <DialogHeader className="pb-4">
          <DialogTitle>
            {mode === "add" ? "Add New Employee" : "Edit Employee"}
          </DialogTitle>
        </DialogHeader>
        <div className="max-h-[75vh] overflow-y-auto pr-2 pb-4">
          <EmployeeForm
            key={formKey}
            initialData={employee}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isEdit={mode === "edit"}
            departments={departments}
            roles={roles}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
